import { get_group_message_filters, update_group_message_filters } from '../networking/apis'
import { NOT_SET } from '../utils/gms_filters'

// the quick windows the page offers; anything else saved is ignored on restore
const QUICK_WINDOWS = ['next_4h', 'rest_of_today', 'tomorrow']

// Remembers the group message sender's filter set between visits. Dispatchers send to the same
// group nearly every time, so rebuilding the filters on each visit is the bulk of the work
// (ticket 1802). Saved on the user's profile rather than in localStorage, which login.vue clears on
// every visit to the login page, so these would reset at each login.
//
// The page using this mixin must define: user_type, message_type, job_title_filters,
// company_location_filters, employment_status_filters, window_preset, and the load_data and
// set_quick_window methods.
export default {
  data() {
    return {
      sticky_filters_loaded: false,
      restoring_sticky_filters: false,
    }
  },

  methods: {
    // filters the user picked before but that this tenant/user type no longer offers would sit in
    // the toggle with no checkbox to clear them, so they're dropped once the options are known
    prune_to_options(picked, options) {
      const available = (options || []).map(option => option.value).filter(value => value !== '')
      // "Not set" is added by the page, not the server, so it's never in the loaded options
      return (picked || []).filter(value => value == NOT_SET || available.indexOf(value) != -1)
    },

    apply_saved_filter_options() {
      this.job_title_filters = this.prune_to_options(this.job_title_filters, this.job_title_options)
      this.company_location_filters = this.prune_to_options(this.company_location_filters, this.location_options)
      this.employment_status_filters = this.prune_to_options(this.employment_status_filters, this.employment_status_options)
    },

    // puts a saved filter set back on the page; separate from the fetch so it can be tested
    apply_saved_filters(saved) {
      if (!saved)
        return
      if (saved.user_type != null && this.user_type_options.some(option => option.value == saved.user_type))
        this.user_type = saved.user_type
      if (saved.message_type == 'SMS' || saved.message_type == 'EMAIL')
        this.message_type = saved.message_type
      // the option lists arrive with the data, so these are pruned in load_data
      this.job_title_filters = saved.job_titles || []
      this.company_location_filters = saved.locations || []
      this.employment_status_filters = saved.employment_statuses || []
      // a quick window comes back relative to now; a custom one falls back to the default
      if (QUICK_WINDOWS.indexOf(saved.window_preset) != -1)
        this.set_quick_window(saved.window_preset)
    
    },

    async load_sticky_filters() {
      // restoring user_type would otherwise trip its watcher into a second load
      this.restoring_sticky_filters = true
      try {
        const { value: { data = {} } = {} } = await get_group_message_filters()
        this.apply_saved_filters((data.group_message_filters || {})[this.user.tenant])
      } catch (e) {
        // remembered filters are a convenience; the page still works without them
      }
      // after the flush, not before it: Vue 2 runs watcher callbacks on nextTick, so clearing the
      // flag synchronously meant every watcher this restore tripped saw it already false. the
      // user_type watcher then fetched the list a second time and the restore saved back what it
      // had just read (FI-2169 review)
      await this.$nextTick()
      this.restoring_sticky_filters = false
      this.sticky_filters_loaded = true
    },

    save_sticky_filters() {
      // nothing to save until the saved set has been read, or the first load would overwrite it
      if (!this.sticky_filters_loaded || !this.user || !this.user.tenant)
        return
      clearTimeout(this._sticky_filters_timer)
      // checking boxes fires one change per click; wait for the user to stop
      this._sticky_filters_timer = setTimeout(() => {
        update_group_message_filters(this.user.tenant, {
          user_type: this.user_type,
          message_type: this.message_type,
          job_titles: this.job_title_filters,
          locations: this.company_location_filters,
          employment_statuses: this.employment_status_filters,
          window_preset: this.window_preset || '',
        }).catch(() => {})
      }, 800)
    },
  },

  beforeDestroy() {
    clearTimeout(this._sticky_filters_timer)
  },
}
